import React, { useState, useEffect, useCallback } from "react";
import { Button, Spinner } from "react-bootstrap";
import {
  FaBookOpen,
  FaLayerGroup,
  FaUserGraduate,
  FaVideo,
  FaCog,
  FaChalkboardTeacher,
  FaMoneyBillWave,
  FaArrowRight,
  FaTimes,
  FaRocket,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import apiClient from "../../utils/apiClient";
import "./OnboardingWizard.scss";

/**
 * OnboardingWizard — Welcome guide shown after first login for new institute owners.
 * Instead of forms, shows clickable quick-action links to guide them.
 *
 * Props:
 *   onComplete  — called when wizard is dismissed
 *   show        — whether to show the wizard
 */

const QUICK_ACTIONS = [
  {
    icon: <FaBookOpen />,
    title: "Create a Course",
    description: "Define what you teach — e.g. \"Web Development\", \"Data Science\".",
    path: "/courses",
    color: "#4f46e5",
  },
  {
    icon: <FaLayerGroup />,
    title: "Create a Batch",
    description: "Group students into batches — e.g. \"Jan 2026 Batch\".",
    path: "/batches",
    color: "#0891b2",
  },
  {
    icon: <FaUserGraduate />,
    title: "Add Students",
    description: "Add students manually or invite them via email.",
    path: "/students",
    color: "#059669",
  },
  {
    icon: <FaChalkboardTeacher />,
    title: "Add Instructors",
    description: "Invite instructors to teach your courses.",
    path: "/instructors",
    color: "#7c3aed",
  },
  {
    icon: <FaVideo />,
    title: "Connect Zoom",
    description: "Set up live classes with Zoom integration.",
    path: "/settings",
    color: "#2563eb",
  },
  {
    icon: <FaCog />,
    title: "Institute Settings",
    description: "Configure branding, fees, and other preferences.",
    path: "/settings",
    color: "#64748b",
  },
];

const OnboardingWizard = ({ onComplete, show }) => {
  const [statusLoading, setStatusLoading] = useState(true);
  const navigate = useNavigate();

  // Fetch onboarding status on mount
  const fetchStatus = useCallback(async () => {
    try {
      setStatusLoading(true);
      const { data } = await apiClient.get("/auth/onboarding-status");

      // If already complete, dismiss the wizard
      if (data.isComplete || data.institute?.onboardingCompleted) {
        onComplete?.();
      }
    } catch {
      // If fetch fails, just show the wizard
    } finally {
      setStatusLoading(false);
    }
  }, [onComplete]);

  useEffect(() => {
    if (show) fetchStatus();
  }, [show, fetchStatus]);

  if (!show) return null;

  const handleDismiss = async () => {
    // Mark onboarding as viewed
    try {
      await apiClient.patch("/auth/onboarding-complete");
    } catch {
      // non-critical
    }
    onComplete?.();
  };

  const handleActionClick = async (path) => {
    // Mark onboarding complete and navigate
    try {
      await apiClient.patch("/auth/onboarding-complete");
    } catch {
      // non-critical
    }
    onComplete?.();
    navigate(path);
  };

  if (statusLoading) {
    return (
      <div className="onboarding-overlay">
        <div className="onboarding-card">
          <div className="text-center py-5">
            <Spinner animation="border" variant="primary" />
            <p className="mt-3 text-muted">Loading your setup...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="onboarding-overlay">
      <div className="onboarding-card onboarding-guide-card">
        {/* Header */}
        <div className="onboarding-header">
          <div>
            <h4 className="onboarding-title">
              <FaRocket className="me-2" style={{ color: "#f59e0b" }} />
              Welcome to Your Institute!
            </h4>
            <p className="onboarding-subtitle">
              Here's how to get started. Click any item to begin.
            </p>
          </div>
          <button className="onboarding-close" onClick={handleDismiss} title="Close — you can find these in the sidebar">
            <FaTimes />
          </button>
        </div>

        {/* Quick Actions Grid */}
        <div className="onboarding-body">
          <div className="onboarding-actions-grid">
            {QUICK_ACTIONS.map((action, idx) => (
              <button
                key={idx}
                className="onboarding-action-card"
                onClick={() => handleActionClick(action.path)}
              >
                <div className="onboarding-action-icon" style={{ background: action.color }}>
                  {action.icon}
                </div>
                <div className="onboarding-action-content">
                  <h6 className="onboarding-action-title">
                    {action.title}
                    <FaArrowRight className="onboarding-action-arrow" />
                  </h6>
                  <p className="onboarding-action-desc">{action.description}</p>
                </div>
              </button>
            ))}
          </div>

          <div className="onboarding-tip">
            <strong>💡 Tip:</strong> You can always access all these from the sidebar menu.
          </div>
        </div>

        {/* Footer */}
        <div className="onboarding-footer">
          <div className="onboarding-footer-right" style={{ width: "100%", justifyContent: "center" }}>
            <Button
              variant="primary"
              onClick={handleDismiss}
              className="onboarding-next-btn"
            >
              Got it, let me explore! <FaArrowRight className="ms-1" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OnboardingWizard;
